import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { AgentQuestionItem, AgentQuestionOption } from '../types';
import { AgentQuestionAnswer } from '../utils/agentQuestions';

type AgentQuestionFormProps = {
  questions: AgentQuestionItem[];
  disabled?: boolean;
  onSubmit: (answers: Record<number, AgentQuestionAnswer>) => void;
};

const hasValidAnswer = (
  question: AgentQuestionItem,
  answer: AgentQuestionAnswer | undefined
): boolean => {
  const customText = answer?.customText?.trim() ?? '';
  if ((question.options ?? []).length === 0) {
    return customText.length > 0;
  }
  if (question.multi_select) {
    return (
      (Array.isArray(answer?.value) && answer.value.length > 0) ||
      customText.length > 0
    );
  }
  return (
    (typeof answer?.value === 'string' && answer.value.length > 0) ||
    customText.length > 0
  );
};

const getQuestionKey = (question: AgentQuestionItem): string =>
  `${question.question}-${question.multi_select}-${(question.options ?? []).map(option => option.value).join('|')}`;

const OptionLabel = ({ option }: { option: AgentQuestionOption }) => (
  <Stack spacing={0.125}>
    <Typography variant="body2">{option.label}</Typography>
    {option.description && (
      <Typography variant="caption" color="text.secondary">
        {option.description}
      </Typography>
    )}
  </Stack>
);

export const AgentQuestionForm = ({
  questions,
  disabled,
  onSubmit,
}: AgentQuestionFormProps) => {
  const intl = useIntl();
  const [answers, setAnswers] = useState<Record<number, AgentQuestionAnswer>>(
    {}
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const questionSignature = useMemo(
    () => questions.map(getQuestionKey).join('||'),
    [questions]
  );

  useEffect(() => {
    setCurrentIndex(0);
  }, [questionSignature]);

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;

  const canSubmit = useMemo(
    () =>
      !disabled &&
      questions.every((question, index) =>
        hasValidAnswer(question, answers[index])
      ),
    [answers, disabled, questions]
  );
  const canProceedCurrent =
    !disabled &&
    currentQuestion !== undefined &&
    hasValidAnswer(currentQuestion, answers[currentIndex]);

  const handleCustomTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      setAnswers(prev => ({
        ...prev,
        [currentIndex]: {
          ...prev[currentIndex],
          customText: value,
        },
      }));
    },
    [currentIndex]
  );

  if (!currentQuestion) {
    return null;
  }

  const options = currentQuestion.options ?? [];
  const selectedValues = Array.isArray(answers[currentIndex]?.value)
    ? answers[currentIndex].value
    : [];

  return (
    <Paper
      component="form"
      elevation={0}
      onSubmit={event => {
        event.preventDefault();
        if (!canSubmit || !isLastQuestion) {
          return;
        }
        onSubmit(answers);
      }}
      sx={{
        p: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: borderRadius('m'),
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="body2" fontWeight={600}>
          {intl.formatMessage(
            { id: 'features.chat.agentQuestion.title' },
            { current: currentIndex + 1, total: questions.length }
          )}
        </Typography>
        <Stack key={getQuestionKey(currentQuestion)} spacing={1}>
          <Typography variant="body2">{currentQuestion.question}</Typography>
          <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: 0.5 }}>
            {options.length === 0 ? (
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                placeholder={intl.formatMessage({
                  id: 'features.chat.agentQuestion.customPlaceholder',
                })}
                value={answers[currentIndex]?.customText ?? ''}
                onChange={handleCustomTextChange}
                disabled={disabled}
              />
            ) : (
              <Stack spacing={1}>
                {currentQuestion.multi_select ? (
                  <Stack>
                    {options.map(option => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            data-cy="chat-agent-question-checkbox"
                            checked={selectedValues.includes(option.value)}
                          />
                        }
                        label={<OptionLabel option={option} />}
                        onChange={(_event, isChecked) => {
                          setAnswers(prev => {
                            const currentValues = Array.isArray(
                              prev[currentIndex]?.value
                            )
                              ? prev[currentIndex].value
                              : [];
                            const nextValues = isChecked
                              ? [...currentValues, option.value]
                              : currentValues.filter(
                                  value => value !== option.value
                                );
                            return {
                              ...prev,
                              [currentIndex]: {
                                ...prev[currentIndex],
                                value: nextValues,
                              },
                            };
                          });
                        }}
                        disabled={disabled}
                      />
                    ))}
                  </Stack>
                ) : (
                  <RadioGroup
                    value={
                      typeof answers[currentIndex]?.value === 'string'
                        ? answers[currentIndex].value
                        : ''
                    }
                    onChange={event => {
                      const { value } = event.target;
                      setAnswers(prev => ({
                        ...prev,
                        [currentIndex]: {
                          ...prev[currentIndex],
                          value,
                        },
                      }));
                    }}
                  >
                    {options.map(option => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio data-cy="chat-agent-question-radio" />}
                        label={<OptionLabel option={option} />}
                        disabled={disabled}
                      />
                    ))}
                  </RadioGroup>
                )}
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  placeholder={intl.formatMessage({
                    id: 'features.chat.agentQuestion.customWithOptionsPlaceholder',
                  })}
                  value={answers[currentIndex]?.customText ?? ''}
                  onChange={handleCustomTextChange}
                  disabled={disabled}
                />
              </Stack>
            )}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            data-cy="chat-agent-question-back"
            type="button"
            variant="outlined"
            disabled={disabled || currentIndex === 0}
            onClick={() => setCurrentIndex(prev => prev - 1)}
          >
            {intl.formatMessage({ id: 'features.chat.agentQuestion.back' })}
          </Button>
          {isLastQuestion ? (
            <Button
              data-cy="chat-agent-question-submit"
              type="submit"
              variant="contained"
              disabled={!canSubmit}
            >
              {intl.formatMessage({ id: 'features.chat.agentQuestion.send' })}
            </Button>
          ) : (
            <Button
              data-cy="chat-agent-question-next"
              type="button"
              variant="contained"
              disabled={!canProceedCurrent}
              onClick={() => setCurrentIndex(prev => prev + 1)}
            >
              {intl.formatMessage({ id: 'features.chat.agentQuestion.next' })}
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};
